import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Form, Modal } from 'react-bootstrap';
import { Post, PostTranslation, LanguageShortcutName, GalleriesFilterResponseData, FileItem, Gallery } from '../../Models/Models';
import { Constants } from '../../Models/Constants';
import { quillModules } from '../../Models/quillModules';
import { SiteContext } from '../../Contexts';
import ReactQuill from 'react-quill';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTrash, faImages, faPen, faPaperclip, faLink,
    faFile, faFilePdf, faFileLines, faEye, faEyeSlash, faBan,
} from '@fortawesome/free-solid-svg-icons';
import ReusableModal from '../Modals/ReusableModal';
import { PostService } from '../../Services/PostService';
import { EntityColors } from '../../Themes/EntityColors';
import GallerySelectorComponent from '../Gallery/GallerySelector';
import { GalleryService } from '../../Services/GalleryService';
import DiscardButton from '../Buttons/DiscardButton';
import SaveButton from '../Buttons/SaveButton';
import DeleteButton from '../Buttons/DeleteButton';
import EditButton from '../Buttons/EditButton';
import { Utils } from '../../Services/Utils';
import CreateButton from '../Buttons/CreateButton';
import GalleryCreateEdit from '../Gallery/GalleryCreateEdit';
import FormShell from '../Form/FormShell';
import RequiredLabel from '../Form/RequiredLabel';
import { useIsDirty } from '../../Hooks/useIsDirty';
import DatePicker from '../DatePicker';
import FileSelectorComponent from '../File/FileSelector';
import { FlagImage, AddLanguageTile, LanguagePickerModal } from '../Language/LanguagePicker';

const PUBLISHED_COLOR = '#1E8A5E';
const NEUTRAL_ACCENT = '#6D57D4';

function todayDateOnly(): string {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${now.getFullYear()}-${month}-${day}`;
}

const PostCreateEditComponent = ({ isEditMode }: { isEditMode: boolean }) => {
    useEffect(() => {
        Utils.setDocumentTitle(isEditMode ? 'Úprava postu' : 'Tvorba postu');
    }, [isEditMode]);

    const { background, action, icon } = EntityColors.blog;
    const { createPost, getPost, updatePost, deletePost } = PostService();
    const { getGallery } = GalleryService();
    const { blogId } = useParams<{ blogId: string }>();
    const { currentSite }: any = useContext(SiteContext);

    const [post, setPost] = useState<Post>(Constants.defaultPost(currentSite));
    const [loaded, setLoaded] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [showGalleryPickerModal, setShowGalleryPickerModal] = useState(false);
    const [showGalleryCreateModal, setShowGalleryCreateModal] = useState(false);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [linkMode, setLinkMode] = useState<'url' | 'file' | null>(null);
    const [showFilePickerModal, setShowFilePickerModal] = useState(false);
    const [statusChosen, setStatusChosen] = useState<'hidden' | 'published' | null>(null);
    const [showAddLanguageModal, setShowAddLanguageModal] = useState(false);
    const [showChangeLanguageModal, setShowChangeLanguageModal] = useState(false);
    const isDirty = useIsDirty(post, loaded);

    const languageShortCutNames = useMemo<readonly LanguageShortcutName[]>(() =>
        (Object.values(LanguageShortcutName) as LanguageShortcutName[])
            .map(short => Utils.capitalizeFirstLetter(short) as LanguageShortcutName),
        []
    );

    useEffect(() => {
        const load = async () => {
            if (isEditMode) {
                const bp = await getPost({ id: Number(blogId), websiteId: currentSite.id });
                const sortedTranslations = [...bp.translations].sort((a, b) =>
                    a.isDefault === b.isDefault ? 0 : a.isDefault ? -1 : 1
                );
                setPost({ ...bp, translations: sortedTranslations });
                setLinkMode(bp.fileId ? 'file' : bp.link ? 'url' : null);
                setStatusChosen(bp.publishedAt ? 'published' : 'hidden');
            }
            setLoaded(true);
        };
        void load();
    }, [isEditMode, blogId]);

    const doSave = async () => {
        if (isEditMode) await updatePost(post);
        else await createPost(post);
    };

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        void doSave();
    };

    const handleDelete = async () => {
        await deletePost(post.id);
    };

    const handleLanguageSelect = (short: LanguageShortcutName) => {
        if (!post.translations.some(t => t.languageShortcut === short)) {
            setPost({
                ...post,
                translations: [
                    ...post.translations,
                    { headline: '', description: '', htmlText: '', languageShortcut: short, postId: post.id ?? 0, isDefault: false },
                ],
            });
        }
        setShowAddLanguageModal(false);
    };

    const handleDefaultLanguageChange = (short: LanguageShortcutName) => {
        const existing = post.translations.filter(t => t.languageShortcut !== short);
        const picked = post.translations.find(t => t.languageShortcut === short) ?? {
            headline: '', description: '', htmlText: '', languageShortcut: short, postId: post.id ?? 0, isDefault: false,
        } as PostTranslation;
        setPost({
            ...post,
            translations: [{ ...picked, isDefault: true }, ...existing.map(t => ({ ...t, isDefault: false }))],
        });
        setShowChangeLanguageModal(false);
    };

    const handleTranslationFieldChange = (
        langShortcut: LanguageShortcutName,
        field: 'headline' | 'description' | 'htmlText',
        value: string
    ) => {
        setPost({
            ...post,
            translations: post.translations.map(t => (t.languageShortcut === langShortcut ? { ...t, [field]: value } : t)),
        });
    };

    const handleRemoveLanguage = (langShortcut: LanguageShortcutName) => {
        setPost({ ...post, translations: post.translations.filter(t => t.languageShortcut !== langShortcut) });
    };

    const addLanguageOptions = languageShortCutNames
        .filter(short => !post.translations.some(t => t.languageShortcut === short));

    const changeDefaultLanguageOptions = languageShortCutNames
        .filter(short => !post.translations.some(t => t.isDefault && t.languageShortcut === short));

    const defaultHeadline = post.translations.find(t => t.isDefault)?.headline ?? post.translations[0]?.headline ?? '';

    return (
        <>
            <FormShell
                title={isEditMode ? 'Editovať príspevok' : 'Vytvoriť príspevok'}
                icon={icon}
                accentColor={action}
                backHref="/post/table"
                isDirty={isDirty}
            >
                {!loaded ? (
                    <p className="text-center py-4 text-secondary">Načítavam...</p>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <div className="st-trans-stack mb-3">
                            {post.translations.map(tr => (
                                <div
                                    key={tr.languageShortcut}
                                    className={`st-trans-card${tr.isDefault ? ' st-trans-card--default' : ''}`}
                                    style={{ '--trans-accent': action } as React.CSSProperties}
                                >
                                    <div className="st-trans-card__head">
                                        <div className="st-trans-card__id">
                                            <FlagImage short={tr.languageShortcut} size="sm" />
                                            <span className="st-trans-card__lang">
                                                {Utils.capitalizeFirstLetter(tr.languageShortcut)}
                                            </span>
                                            {tr.isDefault && (
                                                <span className="st-trans-card__badge">
                                                    Predvolený preklad
                                                </span>
                                            )}
                                        </div>
                                        {tr.isDefault ? (
                                            <EditButton color="gray" text="Zmeniť" onClick={() => setShowChangeLanguageModal(true)} />
                                        ) : (
                                            <DeleteButton iconOnly onClick={() => handleRemoveLanguage(tr.languageShortcut)} />
                                        )}
                                    </div>
                                    <div className="st-trans-card__body">
                                        <Form.Group className="mb-2">
                                            <RequiredLabel>Nadpis</RequiredLabel>
                                            <Form.Control
                                                required
                                                autoFocus={tr.isDefault}
                                                value={tr.headline}
                                                onChange={e => handleTranslationFieldChange(tr.languageShortcut, 'headline', e.target.value)}
                                            />
                                        </Form.Group>

                                        <Form.Group className="mb-2">
                                            <RequiredLabel>Stručný popis</RequiredLabel>
                                            <Form.Control
                                                type="text"
                                                required
                                                value={tr.description}
                                                onChange={e => handleTranslationFieldChange(tr.languageShortcut, 'description', e.target.value)}
                                            />
                                        </Form.Group>

                                        <Form.Group className="text-edit-quill">
                                            <Form.Label>Text</Form.Label>
                                            <ReactQuill
                                                modules={quillModules}
                                                value={tr.htmlText}
                                                onChange={value => handleTranslationFieldChange(tr.languageShortcut, 'htmlText', value)}
                                            />
                                        </Form.Group>
                                    </div>
                                </div>
                            ))}

                            <AddLanguageTile accentColor={action} onClick={() => setShowAddLanguageModal(true)} />
                        </div>

                        <Form.Group className="mb-3">
                            <Form.Label className="mb-2">Odkaz</Form.Label>
                            <div className="d-flex gap-2 mb-2">
                                <StatusOption
                                    active={linkMode === null}
                                    icon={faBan}
                                    label="Žiadny"
                                    color="#64748B"
                                    onClick={() => {
                                        setLinkMode(null);
                                        setPost({ ...post, link: '', fileId: undefined, file: undefined });
                                    }}
                                />
                                <StatusOption
                                    active={linkMode === 'url'}
                                    icon={faLink}
                                    label="Odkaz"
                                    color={NEUTRAL_ACCENT}
                                    onClick={() => {
                                        if (linkMode === 'url') {
                                            setLinkMode(null);
                                            setPost({ ...post, link: '' });
                                        } else {
                                            setLinkMode('url');
                                            setPost({ ...post, link: '', fileId: undefined, file: undefined });
                                        }
                                    }}
                                />
                                <StatusOption
                                    active={linkMode === 'file'}
                                    icon={faPaperclip}
                                    label="Súbor"
                                    color={NEUTRAL_ACCENT}
                                    onClick={() => {
                                        if (linkMode === 'file') {
                                            setLinkMode(null);
                                            setPost({ ...post, fileId: undefined, file: undefined });
                                        } else {
                                            setLinkMode('file');
                                            setPost({ ...post, link: '', fileId: undefined, file: undefined });
                                        }
                                    }}
                                />

                            </div>

                            {linkMode === 'url' && (
                                <div className="st-inline-field">
                                    <FontAwesomeIcon icon={faLink} className="st-inline-field__icon" />
                                    <Form.Control
                                        type="url"
                                        className="placeholder-muted"
                                        value={post.link}
                                        placeholder="Vložte odkaz, napr. https://example.com"
                                        onChange={e => setPost({ ...post, link: e.target.value })}
                                    />
                                </div>
                            )}

                            {linkMode === 'file' && (
                                <FileField
                                    file={post.file}
                                    accentColor={NEUTRAL_ACCENT}
                                    onPick={() => setShowFilePickerModal(true)}
                                    onRemove={() => setPost({ ...post, fileId: undefined, file: undefined })}
                                />
                            )}
                        </Form.Group>

                        {currentSite.settings.usesGallery && (
                            <Form.Group className="mb-3">
                                <Form.Label className="mb-1">Galéria</Form.Label>
                                <GalleryField
                                    gallery={post.gallery}
                                    accentColor={NEUTRAL_ACCENT}
                                    onPick={() => setShowGalleryPickerModal(true)}
                                    onRemove={() => setPost({ ...post, gallery: null, galleryId: undefined })}
                                />
                            </Form.Group>
                        )}

                        <Form.Group className="mb-3">
                            <RequiredLabel className={"mb-2"}>Stav príspevku</RequiredLabel>
                            <div className="d-flex gap-2">
                                <StatusOption
                                    active={statusChosen === 'hidden'}
                                    icon={faEyeSlash}
                                    label="Skryté"
                                    color="#64748B"
                                    onClick={() => {
                                        setStatusChosen('hidden');
                                        setPost({ ...post, publishedAt: undefined });
                                    }}
                                />
                                <StatusOption
                                    active={statusChosen === 'published'}
                                    icon={faEye}
                                    label="Publikované"
                                    color={PUBLISHED_COLOR}
                                    onClick={() => {
                                        setStatusChosen('published');
                                        setPost({ ...post, publishedAt: post.publishedAt ?? todayDateOnly() });
                                    }}
                                />
                            </div>

                            {statusChosen === null && (
                                <p className="st-form-hint">
                                    Zvoľte stav príspevku pred uložením.
                                </p>
                            )}

                            {!!post.publishedAt && (
                                <div
                                    className="st-fade-in st-publish-date"
                                    style={{ '--pub-color': PUBLISHED_COLOR } as React.CSSProperties}
                                >
                                    <Form.Label>
                                        Dátum zverejnenia
                                    </Form.Label>
                                    <DatePicker
                                        value={post.publishedAt}
                                        onChange={next => setPost({ ...post, publishedAt: next })}
                                        accentColor={PUBLISHED_COLOR}
                                    />
                                </div>
                            )}
                        </Form.Group>


                        <div className="d-flex justify-content-center mt-4 gap-3">
                            {isEditMode && <DeleteButton onClick={() => setDeleteModal(true)} />}
                            <DiscardButton to="/post/table" isDirty={isDirty} />
                            <SaveButton
                                type="submit"
                                disabled={statusChosen === null}
                                text={statusChosen === 'published' ? 'Publikovať' : 'Uložiť draft'}
                                onSave={doSave}
                                websiteId={currentSite.id}
                            />
                        </div>
                    </form>
                )}
            </FormShell>

            <ReusableModal
                show={deleteModal}
                title="Naozaj chcete zmazať príspevok?"
                description={`Príspevok "${defaultHeadline}" bude nenávratne odstránený.`}
                onHide={() => setDeleteModal(false)}
                onSubmit={handleDelete}
            />

            <Modal show={showFilePickerModal} onHide={() => setShowFilePickerModal(false)} dialogClassName="modal-lg">
                <Modal.Header closeButton>
                    <Modal.Title>Vyberte súbor</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <FileSelectorComponent
                        accentColor={NEUTRAL_ACCENT}
                        selectedFilePath={post.file?.filePath}
                        handleFileClick={(file: FileItem) => {
                            setPost({ ...post, fileId: file.id, file, link: '' });
                            setShowFilePickerModal(false);
                        }}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowFilePickerModal(false)}>
                        Zavrieť
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showGalleryPickerModal} onHide={() => setShowGalleryPickerModal(false)} dialogClassName="modal-xl">
                <Modal.Header closeButton>
                    <Modal.Title>Vyberte Galériu</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <GallerySelectorComponent
                        handleGalleryClick={async (gal: GalleriesFilterResponseData) => {
                            setPost({ ...post, gallery: await getGallery(gal.id), galleryId: gal.id });
                            setShowGalleryPickerModal(false);
                        }}
                        refreshTrigger={refreshTrigger}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <CreateButton color={NEUTRAL_ACCENT} onClick={() => {
                        setShowGalleryCreateModal(true);
                        setShowGalleryPickerModal(false);
                    }} label="Vytvoriť novú galériu" />
                    <Button variant="secondary" onClick={() => setShowGalleryPickerModal(false)}>
                        Zavrieť
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showGalleryCreateModal} onHide={() => {
                setShowGalleryCreateModal(false);
                setShowGalleryPickerModal(true);
                setRefreshTrigger(refreshTrigger + 1);
            }} dialogClassName="modal-xl">
                <Modal.Body className="pb-4">
                    <GalleryCreateEdit isEditMode={false} isPopUp={true} onClose={() => {
                        setShowGalleryCreateModal(false);
                        setShowGalleryPickerModal(true);
                        setRefreshTrigger(refreshTrigger + 1);
                    }} />
                </Modal.Body>
            </Modal>

            <LanguagePickerModal
                show={showAddLanguageModal}
                title="Vyberte jazyk"
                accentColor={action}
                options={addLanguageOptions}
                onHide={() => setShowAddLanguageModal(false)}
                onPick={handleLanguageSelect}
            />

            <LanguagePickerModal
                show={showChangeLanguageModal}
                title="Vyberte predvolený jazyk"
                accentColor={action}
                options={changeDefaultLanguageOptions}
                onHide={() => setShowChangeLanguageModal(false)}
                onPick={handleDefaultLanguageChange}
            />
        </>
    );
};

const StatusOption = ({
    active,
    icon,
    label,
    color,
    onClick,
}: {
    active: boolean;
    icon: any;
    label: string;
    color: string;
    onClick: () => void;
}) => (
    <button
        type="button"
        onClick={onClick}
        className={`st-opt${active ? ' st-opt--active' : ''}`}
        style={{ '--opt-color': color } as React.CSSProperties}
    >
        <FontAwesomeIcon icon={icon} />
        {label}
    </button>
);

const GalleryField = ({
    gallery,
    accentColor,
    onPick,
    onRemove,
}: {
    gallery?: Gallery | null;
    accentColor: string;
    onPick: () => void;
    onRemove: () => void;
}) => {
    const accentStyle = { '--drop-accent': accentColor } as React.CSSProperties;

    if (gallery == null) {
        return (
            <div
                role="button"
                tabIndex={0}
                onClick={onPick}
                onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onPick();
                    }
                }}
                className="st-dropzone"
                style={accentStyle}
            >
                <FontAwesomeIcon icon={faImages} className="st-dropzone__icon" />
                <span className="st-dropzone__label">Vybrať galériu</span>
            </div>
        );
    }

    return (
        <div className="st-assetcard" style={accentStyle}>
            <div
                role="button"
                tabIndex={0}
                onClick={onPick}
                onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onPick();
                    }
                }}
                title="Zmeniť galériu"
                className="st-assetcard__thumb"
            >
                <img src={gallery.titleImage.filePath} alt={gallery.titleImage.alt} />
                <div className="st-assetcard__overlay">
                    <FontAwesomeIcon icon={faPen} />
                </div>
            </div>

            <div className="st-assetcard__body">
                <p className="st-assetcard__name">{gallery.title}</p>
                <button type="button" onClick={onPick} className="st-assetcard__change">
                    Zmeniť galériu
                </button>
            </div>

            <button
                type="button"
                onClick={onRemove}
                title="Odstrániť galériu"
                className="st-assetcard__remove"
            >
                <FontAwesomeIcon icon={faTrash} />
            </button>
        </div>
    );
};

const FileField = ({
    file,
    accentColor,
    onPick,
    onRemove,
}: {
    file?: FileItem;
    accentColor: string;
    onPick: () => void;
    onRemove: () => void;
}) => {
    const accentStyle = { '--drop-accent': accentColor } as React.CSSProperties;

    if (file == null) {
        return (
            <div
                role="button"
                tabIndex={0}
                onClick={onPick}
                onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onPick();
                    }
                }}
                className="st-dropzone"
                style={accentStyle}
            >
                <FontAwesomeIcon icon={faPaperclip} className="st-dropzone__icon" />
                <span className="st-dropzone__label">Vybrať súbor</span>
            </div>
        );
    }

    const ext = file.fileName.split('.').pop()?.toLowerCase() ?? '';
    const { fileIcon, iconColor } = getFileIconStyle(ext);

    return (
        <div className="st-assetcard" style={accentStyle}>
            <div className="st-assetcard__iconbox" style={{ '--asset-icon': iconColor } as React.CSSProperties}>
                <FontAwesomeIcon icon={fileIcon} />
            </div>

            <div className="st-assetcard__body">
                <p className="st-assetcard__name st-assetcard__name--all">{file.fileName}</p>
                <button type="button" onClick={onPick} className="st-assetcard__change">
                    Zmeniť súbor
                </button>
            </div>

            <button
                type="button"
                onClick={onRemove}
                title="Odstrániť súbor"
                className="st-assetcard__remove"
            >
                <FontAwesomeIcon icon={faTrash} />
            </button>
        </div>
    );
};

function getFileIconStyle(ext: string): { fileIcon: any; iconColor: string } {
    switch (ext) {
        case 'pdf':  return { fileIcon: faFilePdf,   iconColor: '#EF4444' };
        case 'doc':
        case 'docx': return { fileIcon: faFileLines, iconColor: '#3B82F6' };
        case 'xls':
        case 'xlsx': return { fileIcon: faFileLines, iconColor: '#10B981' };
        case 'ppt':
        case 'pptx': return { fileIcon: faFileLines, iconColor: '#F97316' };
        case 'txt':
        case 'csv':  return { fileIcon: faFileLines, iconColor: '#8B5CF6' };
        default:     return { fileIcon: faFile,      iconColor: '#64748B' };
    }
}

export default PostCreateEditComponent;
